import {
  FacebookLoginClient,
  InitParams,
  LoginOptions,
  createScriptEle,
  isFacebookApp,
} from '@greatsumini/react-facebook-login';
import * as Sentry from '@sentry/browser';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { isAndroid } from 'react-device-detect';
import { IHandleStartRegistrationProps } from 'types/ui/Registration';
import { authApiClient } from 'utils/apiClient';
import { FACEBOOK_APP_ID } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';
import { keepQueryParameter } from 'utils/url/keepQueryParameter';

interface FacebookCompletionProps {
  accessToken: string;
  userID: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

const initParams: InitParams = {
  appId: FACEBOOK_APP_ID,
  version: 'v9.0',
  xfbml: false,
  cookie: false,
  localStorage: true,
};

export const useFacebookSignIn = (
  handleSuccessfulLogin: (token: string, name: string) => Promise<void>,
  handleStartRegistration: (
    props: IHandleStartRegistrationProps
  ) => Promise<void>,
  setError: (error?: string) => void
) => {
  const router = useRouter();
  const isRedirected = useRef<boolean>(false);

  const [isFacebookLoading, setIsFacebookLoading] = useState(false);
  const redirect_uri =
    typeof window !== 'undefined'
      ? keepQueryParameter(
          location.origin + location.pathname + location.search,
          'redirect'
        )
      : '/';

  const loginDialogProps = {
    redirect_uri,
    state: 'facebookdirectlogin',
    response_type: 'code',
    client_id: FACEBOOK_APP_ID,
  };

  const registerDialogProps = {
    redirect_uri,
    state: 'facebookdirectregister',
    response_type: 'code',
    client_id: FACEBOOK_APP_ID,
  };

  const loadFB = async () => {
    const language = 'en_US';
    await createScriptEle(
      'facebook-jssdk',
      `https://connect.facebook.net/${language}/all.js`
    );

    await FacebookLoginClient.loadSdk(language);

    FacebookLoginClient.init(initParams);
  };

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    setTimeout(loadFB, 500);
  }, [router.isReady]);

  const completeSignIn = async (response: {
    accessToken: string;
    userID: string;
    email: string;
    first_name: string;
    last_name: string;
  }) => {
    if (!response.accessToken || !response.userID) {
      Sentry.captureMessage(
        `Facebook login failed no response.accessToken ${response.accessToken} or response.userID ${response.userID}`
      );
      return;
    }

    const loginResponse = await authApiClient.public.loginWithFacebook({
      body: {
        accessToken: response.accessToken,
      },
    });

    if (loginResponse.status === HttpStatusCode.OK) {
      await handleSuccessfulLogin(
        loginResponse.body.token,
        loginResponse.body.welcomeName
      );
    } else {
      Sentry.captureMessage(
        `loginResponse not successful ${loginResponse.body.message}`
      );

      setError(loginResponse.body.message);
    }
  };

  const completeRegistration = async (response: {
    accessToken: string;
    userID: string;
    email: string;
    first_name: string;
    last_name: string;
  }) => {
    if (!response.accessToken || !response.userID) {
      Sentry.captureMessage(
        `Facebook login failed no response.accessToken ${response.accessToken} or response.userID ${response.userID}`
      );
      return;
    }

    const registrationResult =
      await authApiClient.public.validateFacebookAccessToken({
        body: {
          accessToken: response.accessToken,
          userId: response.userID,
          email: response.email,
        },
      });

    if (registrationResult.status === HttpStatusCode.BAD_REQUEST) {
      const errorString = registrationResult.body.message;
      setError(errorString);
      return;
    } else if (registrationResult.status === HttpStatusCode.OK) {
      handleSuccessfulLogin(
        registrationResult.body.token,
        registrationResult.body.welcomeName
      );
      return;
    }

    handleStartRegistration({
      facebookUserId: response.userID,
      email: response.email ?? '',
      firstName: response.first_name ?? '',
      lastName: response.last_name ?? '',
    });
  };

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const isLoginRedirected =
      FacebookLoginClient.isRedirected(loginDialogProps);
    const isRegisterRedirected =
      FacebookLoginClient.isRedirected(registerDialogProps);

    isRedirected.current = isLoginRedirected || isRegisterRedirected;
    if (isRedirected.current) {
      setIsFacebookLoading(true);
      const loadProfile = async () => {
        const code = router.query.code;
        const redirect_uri = isLoginRedirected
          ? loginDialogProps.redirect_uri
          : registerDialogProps.redirect_uri;

        // ENG-394 - commented out to remove compile errors. See DAY-139 for ticket that should implement Facebook
        // const profileResponse = await getFacebookProfileFromCode(
        //   code,
        //   redirect_uri
        // );

        // if (!profileResponse.reason) {
        //   if (isLoginRedirected) {
        //     completeSignIn({
        //       accessToken: profileResponse.access_token,
        //       userID: profileResponse.id,
        //       email: profileResponse.email,
        //       first_name: profileResponse.first_name,
        //       last_name: profileResponse.last_name,
        //     });
        //   } else {
        //     completeRegistration({
        //       accessToken: profileResponse.access_token,
        //       userID: profileResponse.id,
        //       email: profileResponse.email,
        //       first_name: profileResponse.first_name,
        //       last_name: profileResponse.last_name,
        //     });
        //   }
        // } else {
        //   console.log(
        //     'Facebook redirect login failed: ',
        //     profileResponse.reason
        //   );
        // }
        // setIsFacebookLoading(false);
      };

      loadProfile();
    }
  }, [router.isReady]);

  const doFacebookSignIn = () => {
    const loginOptions: LoginOptions = {
      scope: 'public_profile,email',
    };
    setIsFacebookLoading(true);

    if (isFacebookApp() || isAndroid) {
      FacebookLoginClient.redirectToDialog(loginDialogProps, loginOptions);
    } else {
      FacebookLoginClient.login((loginResponse) => {
        if (isRedirected.current) {
          console.log('Facebook login onSucccess aborted: redirected');
          return;
        }

        if (!loginResponse.authResponse) {
          console.log('Login Failed!', loginResponse.status);
          setIsFacebookLoading(false);

          return;
        }

        const authResponse = loginResponse.authResponse;

        FacebookLoginClient.getProfile(
          (res: any) => {
            setIsFacebookLoading(false);

            completeSignIn({
              accessToken: authResponse.accessToken,
              userID: authResponse.userID,
              email: res.email,
              first_name: res.first_name,
              last_name: res.last_name,
            });
          },
          { fields: 'id,email,first_name,last_name' }
        );
      }, loginOptions);
    }
  };

  const doFacebookRegister = () => {
    const loginOptions: LoginOptions = {
      scope: 'public_profile,email',
    };

    setIsFacebookLoading(true);

    if (isFacebookApp() || isAndroid) {
      FacebookLoginClient.redirectToDialog(registerDialogProps, loginOptions);
    } else {
      FacebookLoginClient.login((loginResponse) => {
        if (isRedirected.current) {
          console.log('Facebook login onSucccess aborted: redirected');
          return;
        }

        if (!loginResponse.authResponse) {
          setError('Facebook login failed');
          setIsFacebookLoading(false);
          Sentry.captureMessage(
            `Facebook login failed no authResponse ${loginResponse.status}`
          );
          console.log('Login Failed!', loginResponse.status);
          return;
        }

        const authResponse = loginResponse.authResponse;

        FacebookLoginClient.getProfile(
          (res: any) => {
            setIsFacebookLoading(false);
            completeRegistration({
              accessToken: authResponse.accessToken,
              userID: authResponse.userID,
              email: res.email,
              first_name: res.first_name,
              last_name: res.last_name,
            });
          },
          { fields: 'id,email,first_name,last_name' }
        );
      }, loginOptions);
    }
  };

  return {
    isFacebookLoading,
    doFacebookSignIn,
    doFacebookRegister,
  };
};
